import { createFileRoute, Navigate, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import apiClient from '@/api/client'
import type { LoginRequest, LoginResponse, APIResponse } from '@/types'
import { Eye, EyeOff, Store, Users, CreditCard, BarChart3, ChefHat, UserCheck, Settings } from 'lucide-react'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const router = useRouter()
  const [formData, setFormData] = useState<LoginRequest>({ username: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response: APIResponse<LoginResponse> = await apiClient.login(credentials)
      return response
    },
    onSuccess: (data) => {
      console.log('Login success:', data)
      console.log('Current API URL:', import.meta.env.VITE_API_URL)
      if (data.success && data.data) {
        apiClient.setAuthToken(data.data.token)
        localStorage.setItem('pos_user', JSON.stringify(data.data.user))
        console.log('Auth token set, redirecting to home...')
        setTimeout(() => {
          router.navigate({ to: '/' })
        }, 100)
      } else {
        console.error('Login failed:', data)
        setError(data.message || 'Error al iniciar sesión')
      }
    },
    onError: (error: any) => {
      setError(error.message || 'Error al iniciar sesión')
    },
  })

  if (apiClient.isAuthenticated()) {
    return <Navigate to="/" />
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!formData.username || !formData.password) {
      setError('Usuario y contraseña son obligatorios')
      return
    }

    loginMutation.mutate(formData)
  }

  const fillDemoCredentials = (username: string, password: string) => {
    setFormData({ username, password })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-700 p-12 text-white relative overflow-hidden">
        <div className="relative z-10 flex flex-col justify-center max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Store className="w-7 h-7" />
            </div>
            <h1 className="text-3xl font-bold">Sistema POS</h1>
          </div>
          
          <h2 className="text-4xl font-bold mb-6 leading-tight">
            Punto de venta moderno
            <br />
            <span className="text-blue-200">para tu negocio</span>
          </h2>
          
          <p className="text-xl text-blue-100 mb-12 leading-relaxed">
            Gestiona pedidos, inventario y ventas desde un solo lugar.
          </p>

          <div className="grid grid-cols-2 gap-6">
            {[
              { icon: Users, title: 'Personal', desc: 'Acceso por roles' },
              { icon: CreditCard, title: 'Pagos', desc: 'Varios métodos de pago' },
              { icon: BarChart3, title: 'Análisis', desc: 'Informes en tiempo real' },
              { icon: Store, title: 'Pedidos', desc: 'Flujo de cocina' },
            ].map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{feature.title}</h3>
                  <p className="text-blue-200 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Sponsor Banner */}
          <div className="mt-12 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg">
            <div className="text-center mb-3">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1 rounded-full text-xs font-semibold">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                Patrocinado por MadeByAris
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-white/15 rounded-lg p-3 border border-white/20">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 bg-white/30 rounded-md flex items-center justify-center flex-shrink-0">
                    <Store className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white text-sm mb-1">¿Necesitas un MVP o proyecto?</h3>
                    <a 
                      href="https://madebyaris.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs font-medium text-blue-200 hover:text-white transition-colors"
                    >
                      madebyaris.com →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white/15 rounded-lg p-3 border border-white/20">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 bg-white/30 rounded-md flex items-center justify-center flex-shrink-0">
                    <Users className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white text-sm mb-1">Bootcamp de programación</h3>
                    <a 
                      href="https://bootcamp.madebyaris.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs font-medium text-blue-200 hover:text-white transition-colors"
                    >
                      bootcamp.madebyaris.com →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full"
               style={{
                 backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                 backgroundSize: '50px 50px'
               }} />
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-8">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                <Store className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">Inicio de sesión POS</CardTitle>
              <CardDescription className="text-base">
                🍽️ Elige un rol abajo o inicia sesión manualmente
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Usuario</label>
                  <Input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    className="h-11"
                    autoComplete="username"
                    disabled={loginMutation.isPending}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Contraseña</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="h-11 pr-10"
                      autoComplete="current-password"
                      disabled={loginMutation.isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-gradient-to-r from-red-50 to-red-25 border border-red-200 text-red-700 p-4 rounded-lg text-sm shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                      </div>
                      <span className="font-medium">Error al iniciar sesión</span>
                    </div>
                    <div className="mt-1 text-xs text-red-600">{error}</div>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-base font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      Iniciando sesión...
                    </div>
                  ) : (
                    'Iniciar sesión'
                  )}
                </Button>
              </form>

              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-700">Cuentas demo de acceso rápido</h3>
                  <div className="text-xs text-gray-500">Clic para iniciar sesión</div>
                </div>
                
                {/* Featured Roles - Server & Cashier */}
                <div className="mb-4">
                  <div className="text-xs text-gray-600 mb-2 font-medium">🌟 Roles destacados</div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { 
                        username: 'server1', 
                        role: 'Mesero', 
                        icon: UserCheck,
                        bg: 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 border-purple-200', 
                        desc: '🍽️ Servicio en mesa y comedor', 
                        password: 'admin123',
                        features: ['Mesas', 'Pedidos', 'Atención al cliente']
                      },
                      { 
                        username: 'counter1', 
                        role: 'Caja', 
                        icon: CreditCard,
                        bg: 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border-green-200', 
                        desc: '💰 Pagos y todos los pedidos', 
                        password: 'admin123',
                        features: ['Todos los pedidos', 'Pagos', 'Recibos']
                      },
                    ].map((account) => (
                      <button
                        key={account.username}
                        onClick={() => fillDemoCredentials(account.username, account.password)}
                        className={`p-4 rounded-xl border-2 ${account.bg} hover:scale-105 text-left transition-all duration-200 shadow-sm hover:shadow-md`}
                        disabled={loginMutation.isPending}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-white/70 rounded-lg flex items-center justify-center flex-shrink-0">
                            <account.icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="font-semibold text-sm">{account.role}</div>
                              <div className="text-xs opacity-60 font-mono">{account.password}</div>
                            </div>
                            <div className="text-xs mb-2 opacity-80">{account.desc}</div>
                            <div className="flex flex-wrap gap-1">
                              {account.features.map((feature, idx) => (
                                <span key={idx} className="text-[10px] bg-white/50 px-2 py-0.5 rounded-full">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Other Roles */}
                <div>
                  <div className="text-xs text-gray-600 mb-2 font-medium">Otras cuentas demo</div>
                  <div className="grid gap-2">
                    {[
                      { username: 'admin', role: 'Administrador', icon: Settings, bg: 'bg-red-50 text-red-700 border-red-100', desc: '👑 Acceso completo al sistema', password: 'admin123' },
                      { username: 'manager1', role: 'Gerente', icon: BarChart3, bg: 'bg-blue-50 text-blue-700 border-blue-100', desc: '📊 Gestión e informes', password: 'admin123' },
                      { username: 'kitchen1', role: 'Cocina', icon: ChefHat, bg: 'bg-orange-50 text-orange-700 border-orange-100', desc: '👨‍🍳 Preparación de pedidos', password: 'admin123' },
                    ].map((account) => (
                      <button
                        key={account.username}
                        onClick={() => fillDemoCredentials(account.username, account.password)}
                        className={`flex items-center justify-between p-3 border rounded-lg ${account.bg} hover:bg-opacity-80 text-left transition-all duration-200`}
                        disabled={loginMutation.isPending}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-white/70 rounded flex items-center justify-center">
                            <account.icon className="w-3 h-3" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">{account.role}</div>
                            <div className="text-xs opacity-70">{account.desc}</div>
                          </div>
                        </div>
                        <div className="text-xs opacity-60 font-mono">{account.password}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mobile Sponsor Banner */}
          <div className="mt-8 w-full max-w-md lg:hidden">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-lg">
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-md">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  Patrocinado por MadeByAris
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-white/70 rounded-xl p-4 border border-amber-200/50">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Store className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 mb-1">¿Necesitas un MVP o proyecto?</h3>
                      <a 
                        href="https://madebyaris.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        madebyaris.com →
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white/70 rounded-xl p-4 border border-amber-200/50">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 mb-1">Bootcamp de programación</h3>
                      <a 
                        href="https://bootcamp.madebyaris.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                      >
                        bootcamp.madebyaris.com →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
